import { useEffect, useMemo } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  Navigate,
  RouterProvider,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { api, ApiError, ContractError } from "@/lib/api";
import { keys } from "@/lib/query-keys";
import { useMe, useSignOut } from "@/lib/session";
import { meResponse, type Me, type Membership } from "@sitebrew/contracts/v1";
import { useSite } from "@/lib/sites";
import { Shell } from "@/components/shell";
import { QueryBoundary } from "@/components/query-boundary";
import { LoginScreen, type LoginMode } from "@/screens/login";
import { ConsentScreen } from "@/screens/consent";
import { InviteAcceptScreen } from "@/screens/invite-accept";
import { SelectWorkspaceScreen } from "@/screens/select-workspace";
import { authorizationReturn, resumedAuthorization, sameOriginPath } from "@/lib/utils";
import { NoWorkspaceScreen } from "@/screens/signed-in";
import { SitesScreen, UnknownWorkspaceScreen } from "@/screens/sites";
import { NoSuchSiteScreen, SiteScreen } from "@/screens/site";
import { LoginCard, LoginCardSubtitle, LoginCardTitle } from "@/components/ui/login-card";
import {
  LayoutBase,
  LayoutContent,
  LayoutHeader,
  LayoutNotice,
  LayoutSidebar,
  LayoutSidebarContent,
  LayoutSidebarFooter,
  LayoutSidebarHeader,
  PageFrameProvider,
  usePageFrameValue,
} from "@/components/layout";
import {
  addressed,
  declaredRoutes,
  sidebarItems,
  visibleTo,
  type SidebarStaticData,
} from "@/lib/sidebar-items";
import { ButtonLink } from "@/components/button";
import { BrandMark } from "@/components/brand-mark";
import { WorkspacesProvider, type Workspace } from "@/contexts/WorkspaceContext";
import { WorkspaceSwitch } from "@/components/user-management/WorkspaceSwitch";
import { useSelectedWorkspace } from "@/hooks/user-management/useSelectedWorkspace";
import { useTranslation } from "@/hooks/useTranslation";
import {
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Globe, Settings } from "lucide-react";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { SettingsLayout } from "@/components/user-management/SettingsLayout";
import { APITokensSettings } from "@/components/user-management/APITokensSettings";
import { ConnectedApplicationsSettings } from "@/components/user-management/ConnectedApplicationsSettings";
import { MembersSettings } from "@/components/user-management/MembersSettings";
import { ourSettingsItems } from "@/lib/settings-menu";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A console is read while it is open. Refetching on every focus change
      // makes the API busy in proportion to how many tabs somebody left open,
      // which is not a proportion to anything real.
      refetchOnWindowFocus: false,
      // Nor an answer this build cannot read: the API will answer the same
      // shape again, and retrying only delays the screen that says so.
      retry: (count, err) =>
        !(err instanceof ApiError && err.status < 500) && !(err instanceof ContractError) && count < 2,
    },
  },
});

/**
 * Is somebody signed in? Asked in `beforeLoad`, so the answer decides which
 * screen is even mounted rather than being checked inside one.
 *
 * `ensureQueryData` and not a bare fetch: the screen that renders next asks the
 * same query key, so the redirect and the render share one request.
 */
async function currentUser(): Promise<Me | null> {
  return queryClient.ensureQueryData({
    queryKey: keys.me(),
    queryFn: async () => {
      try {
        return await api("/me", meResponse);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
  });
}

/**
 * What a route may say about its place in the left menu.
 *
 * Declared on the router rather than kept in a list beside it: the menu is
 * derived from these (`lib/sidebar-items.ts`), so a screen that can be
 * navigated to and a row that navigates there are the same statement.
 */
declare module "@tanstack/react-router" {
  interface StaticDataRouteOption extends SidebarStaticData {}
}

/**
 * The root draws nothing of its own.
 *
 * `Shell` — the wordmark over a centred card — used to be here, which was right
 * while every screen was a card. It is the signed-out frame now: the signed-in
 * one is `Layout`, and left at the root the two stacked, so the console drew its
 * own name twice and centred the sidebar's neighbour inside a column meant for a
 * card. A screenshot found it and nothing else would have.
 */
const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: () => (
    <Shell>
      <LoginCard>
        <LoginCardTitle>404</LoginCardTitle>
        <LoginCardSubtitle>That page doesn't exist.</LoginCardSubtitle>
        <ButtonLink className="mt-[.9rem] w-full" href="/login">Go to sign in</ButtonLink>
      </LoginCard>
    </Shell>
  ),
});

/**
 * Everything behind the session, in one place.
 *
 * A pathless layout route rather than the same `beforeLoad` copied onto four
 * screens: the check that has to hold for all of them is written once, so a
 * screen added later cannot be added *without* it. Its children read `me` from
 * the cache it has already filled.
 */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authed",
  beforeLoad: async () => {
    if (!(await currentUser())) throw redirect({ to: "/login" });
  },
  component: function Authed() {
    const me = useMe();
    const navigate = useNavigate();

    /**
     * `beforeLoad` decides who may *arrive* here. It cannot decide who may
     * *stay*: it runs on navigation, and a session ending is a change of state
     * with no navigation — signing out, or an expired cookie found by the next
     * refetch. Both leave `me.data` at `null` on a route that has nothing to
     * show for it, and the screen rendered blank: the person was signed out
     * and nobody told them, which reads as a broken button (review of #8).
     *
     * So leaving is asserted here rather than in the sign-out button. The
     * button is one way to end a session and the rarer one; the rule is about
     * the state, not about what caused it. On the layout it now covers the
     * sites screens too, where the same blank page would be worse: the person
     * is mid-task rather than looking at their own address.
     */
    useEffect(() => {
      if (me.data === null) void navigate({ to: "/login" });
    }, [me.data, navigate]);

    return (
      <QueryBoundary query={me}>
        {(data) => (data ? <SignedIn me={data} /> : null)}
      </QueryBoundary>
    );
  },
});

/**
 * The signed-in frame: sidebar, header, screen.
 *
 * `useParams({ strict: false })` because this sits above every authenticated
 * route and the parameters differ between them — the menu asks what the
 * current address holds, and leaves out an item it cannot address (a
 * workspace-scoped row has nothing to point at from a page with no workspace
 * in it).
 */
function SignedIn({ me }: { me: Me }) {
  return (
    <PageFrameProvider>
      <SignedInFrame me={me} />
    </PageFrameProvider>
  );
}

function SignedInFrame({ me }: { me: Me }) {
  const urlParams = useParams({ strict: false });
  const frame = usePageFrameValue();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [storedWorkspaceId, setSelectedWorkspaceId] = useSelectedWorkspace();

  /*
   * What the address holds, and what the screen inside it published on top.
   *
   * A site's page is `/sites/$siteId` — no workspace in it, because the API
   * resolves membership from the site itself (§2). Read from the URL alone the
   * frame therefore forgot which workspace it was in: the subtitle went, the
   * role fell back to the narrower one, and the `Sites` row — the way back —
   * disappeared from the menu on the one screen you reach *from* that list
   * (review of #10). The console knows the answer; it is in the site it just
   * loaded.
   */
  const params = frame.workspaceId ? { ...urlParams, workspaceId: frame.workspaceId } : urlParams;
  const workspace = me.workspaces.find((each) => each.id === params.workspaceId);

  /*
   * Which workspace the account menu's *Settings* row points at.
   *
   * The same landing `/` picks, and for the same reason: the screens behind the
   * row are addressed by workspace, so the row needs one to point at even on a
   * screen whose own address has none (`/sites/$siteId`). The stored choice is
   * checked against the memberships rather than trusted — an id left in the
   * browser from a workspace this account has since left would otherwise send
   * somebody to a screen the API answers `404` for.
   *
   * `undefined` for an account in no workspace, which is what hides the row.
   */
  const settingsWorkspaceId = (
    workspace ??
    me.workspaces.find((each) => each.id === storedWorkspaceId) ??
    me.workspaces[0]
  )?.id;

  const routes = useMemo(() => declaredRoutes(routeTree), []);

  const items = useMemo(
    () =>
      // The role is the current workspace's, and `member` where there is no
      // workspace in the address: the narrower of the two, since a menu is not
      // where access is decided and guessing upwards would only mean offering
      // a click that ends in a refusal.
      visibleTo(sidebarItems(routes), workspace?.role ?? "member")
        .map((item) => addressed(item, params))
        .filter((item): item is NonNullable<typeof item> => item !== null),
    [routes, params, workspace?.role],
  );

  /*
   * The memberships as the fleet's switch wants them: `logo` and `plan` are its
   * two slots for whatever a workspace is identified by, and the fleet puts a
   * word about the workspace in the second one. Here that word is the role.
   *
   * Memoised because the provider watches this array: a fresh one on every
   * render is a fresh dependency on every render.
   */
  const switchable = useMemo<Workspace[]>(
    () =>
      me.workspaces.map((membership) => ({
        id: membership.id,
        name: membership.name,
        logo: BrandMark,
        plan: membership.role,
      })),
    [me.workspaces],
  );

  return (
    <WorkspacesProvider
      workspaces={switchable}
      /*
       * The stored choice, as the fleet has it — with the address ahead of it
       * where the address has one.
       *
       * The fleet's only answer to "which workspace am I in" is
       * `localStorage`, because none of its addresses carry one. Ours do
       * (`/workspaces/$workspaceId/sites`, design note 0001 §2), and a link
       * somebody was sent has to win over what their browser remembers — or the
       * header names one workspace while the screen shows another.
       */
      selectedWorkspaceId={workspace?.id ?? storedWorkspaceId ?? undefined}
      /*
       * The fleet writes the choice and reloads the page; the reload is how its
       * screens pick the new workspace up. Ours are addressed by workspace, so
       * the same job is done by navigating — and the choice is still written,
       * because that is what `/` reads to know where to send somebody next.
       */
      onWorkspaceChange={(picked) => {
        setSelectedWorkspaceId(picked.id);
        void navigate({ to: "/workspaces/$workspaceId/sites", params: { workspaceId: picked.id } });
      }}
    >
      <LayoutBase>
        <LayoutSidebar>
          <LayoutSidebarHeader>
            <WorkspaceSwitch
              sidebarComponents={{ SidebarMenu, SidebarMenuItem, SidebarMenuButton }}
            />
          </LayoutSidebarHeader>
          <LayoutSidebarContent items={items} />
          <LayoutSidebarFooter
            user={{ name: me.user.email }}
            signOut={() => signOut.mutate()}
          >
            {/*
              Settings is reached from the account menu and not from the left
              one — the fleet's own arrangement (`frontend-app/src/routes/
              _layout.tsx`), and the slot for it was already in the dropdown we
              copied, so nothing is bent to fit (`design/0003`).

              Absent for an account in no workspace: the screens behind it are
              addressed by workspace, so there would be nowhere to go. A menu
              row that cannot be pointed anywhere is the same mistake the left
              menu avoids by dropping items it cannot address.
            */}
            {settingsWorkspaceId ? (
              <DropdownMenuItem
                onClick={() =>
                  void navigate({
                    to: "/workspaces/$workspaceId/settings/api-tokens",
                    params: { workspaceId: settingsWorkspaceId },
                  })
                }
                className="cursor-pointer"
              >
                <Settings />
                {t("sideBar.userNav.settings")}
              </DropdownMenuItem>
            ) : null}
          </LayoutSidebarFooter>
        </LayoutSidebar>
        {/*
          `min-w-0`, or the pane grows to fit its widest child instead of the
          window: a flex item's floor is its content, so a 200 character name in
          the trail pushed the whole console sideways and the header's own
          `overflow-hidden` clipped nothing, because nothing was overflowing —
          the pane had simply become wider than the screen (review of #10).
        */}
        <SidebarInset className="min-w-0">
          {/*
            Beside the screen and not in the sidebar: on a phone the sidebar is
            a sheet, so a sentence inside it disappears when the sheet is closed
            — and this one is about a session that did not end.
          */}
          {signOut.isError ? (
            <LayoutNotice>Signing out did not go through — you are still signed in.</LayoutNotice>
          ) : null}
          <LayoutHeader />
          <LayoutContent>
            <Outlet />
          </LayoutContent>
        </SidebarInset>
      </LayoutBase>
    </WorkspacesProvider>
  );
}

/**
 * `/` is a door, not a room.
 *
 * It used to list the workspaces, with a row for it in the menu. The fleet has
 * neither: you are always *in* a workspace, and which one is the last one you
 * picked. So this sends somebody on — to the stored choice where there is one,
 * and to the first workspace otherwise, which is the same fallback the fleet's
 * `useSuspenseUserData` makes.
 *
 * Not `beforeLoad`: the choice lives in `localStorage` and `useSelectedWorkspace`
 * is the one thing that reads it. A second reader would be a second encoding to
 * keep in step with the first.
 *
 * An account in no workspace at all stays here, because there is nowhere to go
 * — the fleet sends that person to onboarding, which this console does not have.
 */
const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  // A title and no `showInSidebar`: nobody navigates here on purpose any more,
  // but the account with no workspace stops here, and a screen the trail cannot
  // name is a screen with no trail at all.
  staticData: { title: "Workspaces" },
  component: function Home() {
    const me = useMe();
    const [storedWorkspaceId] = useSelectedWorkspace();
    const workspaces = me.data?.workspaces ?? [];

    const landing =
      workspaces.find((each) => each.id === storedWorkspaceId) ?? workspaces[0];

    if (!landing) return <NoWorkspaceScreen />;

    return <Navigate to="/workspaces/$workspaceId/sites" params={{ workspaceId: landing.id }} replace />;
  },
});

/**
 * The workspace behind an id in the URL.
 *
 * From `/me`, which is the same membership list the API checks against — so an
 * id nobody is a member of gets the answer the API would give without asking
 * it, and without confirming to the person trying ids that it exists (§6).
 */
function useWorkspace(workspaceId: string): Membership | undefined {
  return useMe().data?.workspaces.find((workspace) => workspace.id === workspaceId);
}

/**
 * The sites of a workspace, and the form that adds one.
 *
 * The workspace is a path segment because it is one in the API (design note
 * 0001 §2): which workspace an action happens in is chosen by the caller, so it
 * is visible in the address rather than implied by a selection held elsewhere.
 */
const sitesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/workspaces/$workspaceId/sites",
  staticData: { title: "Sites", icon: <Globe />, showInSidebar: true, sidebarOrder: 2 },
  component: function Sites() {
    const { workspaceId } = useParams({ from: sitesRoute.id });
    const workspace = useWorkspace(workspaceId);

    // The fetch lives one component down rather than beside this check, because
    // a hook cannot be skipped: called here it would run for a workspace this
    // account is not in, and ask the API a question we have already answered
    // from `/me`. Returning early is what keeps the request from happening.
    return workspace ? <WorkspaceSites workspace={workspace} /> : <UnknownWorkspaceScreen />;
  },
});

/*
 * No `QueryBoundary` around the list any more: the table fetches its own pages
 * (`sitesPage`), so there is no single query for a boundary to wait on — and
 * `DataTable` draws both states itself, the veil while a page is in flight and
 * the API's own sentence in place of the rows when one fails. A boundary here
 * would blank the search box and the pager along with the rows, which is the
 * screen throwing away what somebody typed because a refetch failed.
 */
function WorkspaceSites({ workspace, creating }: { workspace: Membership; creating?: boolean }) {
  return <SitesScreen workspace={workspace} creating={creating} />;
}

/**
 * Creating a site: the list, with the form open over it.
 *
 * The dialog is a route rather than a piece of the list's state, so the address
 * says what is on screen — a link into it opens the form, and the back button
 * closes it. What is behind it is the list it adds to, which is why this route
 * renders the same screen rather than one of its own.
 */
const newSiteRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/workspaces/$workspaceId/sites/new",
  // A title without `showInSidebar`: it is a step in the trail, not a menu row.
  staticData: { title: "New site" },
  component: function NewSite() {
    const { workspaceId } = useParams({ from: newSiteRoute.id });
    const workspace = useWorkspace(workspaceId);

    return workspace ? (
      <WorkspaceSites workspace={workspace} creating />
    ) : (
      <UnknownWorkspaceScreen />
    );
  },
});

/**
 * One site, addressed without its workspace — as `/v1/sites/:site` is.
 *
 * The API resolves membership from the site itself there, so the console does
 * not need the workspace in this URL and does not put it in: a second way to
 * address the same thing is a second thing to keep in step.
 */
const siteRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/sites/$siteId",
  /*
   * A site's page is not under the list's path — it is addressed without its
   * workspace, as `/v1/sites/:site` is — so the trail cannot find its way up by
   * prefix. `parent` is that one missing edge, named as a route rather than as
   * a path, and `test/breadcrumbs.test.tsx` fails if it names one that is not
   * in the tree.
   */
  staticData: { title: "Site", parent: "/_authed/workspaces/$workspaceId/sites" },
  component: function OneSite() {
    const { siteId } = useParams({ from: siteRoute.id });
    const site = useSite(siteId);

    // `404` is an answer — archived, or not this account's — not a failure to
    // load. Handing it to `QueryBoundary` would offer "try again" for something
    // no amount of trying changes.
    if (site.error instanceof ApiError && site.error.status === 404) return <NoSuchSiteScreen />;

    return <QueryBoundary query={site}>{(data) => <SiteScreen site={data} />}</QueryBoundary>;
  },
});

/**
 * The API keys of a workspace, inside the settings frame.
 *
 * Addressed by workspace where the fleet's `/settings/api-tokens` is not
 * (`SettingsLayout` says why at length): a key grants full access to exactly
 * one workspace, and the address is what says *which* — so somebody who works
 * in two cannot issue a key in one while reading the other, and a revocation
 * lands where it was aimed.
 *
 * What it is **not** is the boundary, and that is worth writing down where
 * somebody might lean on it: the membership check below and the API's own
 * refusal are what keep another account's keys off this screen, and
 * `localStorage` can only ever hold a workspace this account is in. Describing
 * the address as the thing that protects the listing is how the check
 * underneath it gets weakened later (`sitebrew-console#11`, DevGuru).
 *
 * Not `showInSidebar`: settings is reached from the account menu, which is the
 * fleet's own arrangement and the one the copied dropdown already had a slot
 * for. A title all the same, because the trail names the screen.
 *
 * The membership check is `sitesRoute`'s, for the reason given there — a
 * workspace this account is not in gets the answer `/me` already holds, without
 * asking the API a question it would refuse.
 */
const apiTokensRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/workspaces/$workspaceId/settings/api-tokens",
  staticData: { title: "API Tokens" },
  component: function ApiTokens() {
    const { workspaceId } = useParams({ from: apiTokensRoute.id });
    const workspace = useWorkspace(workspaceId);

    return workspace ? (
      <SettingsLayout workspaceId={workspace.id} additionalMenuItems={ourSettingsItems(workspace.id)}>
        <APITokensSettings workspaceId={workspace.id} />
      </SettingsLayout>
    ) : (
      <UnknownWorkspaceScreen />
    );
  },
});

/**
 * A workspace's members and pending invites (`sitebrew-api#252`, M3 of design
 * note 0001) — the console half of `sitebrew-api#259`/`#260`'s API.
 *
 * Addressed by workspace for the same reason `apiTokensRoute` is: membership
 * is a fact about one workspace, and the address is what says which one is
 * being read — not a boundary in itself (`sitebrew-console#11`, DevGuru,
 * quoted at length on `apiTokensRoute` above), just where the API's own check
 * is asked the right question.
 */
const membersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/workspaces/$workspaceId/settings/members",
  staticData: { title: "Members" },
  component: function Members() {
    const { workspaceId } = useParams({ from: membersRoute.id });
    const workspace = useWorkspace(workspaceId);

    return workspace ? (
      <SettingsLayout workspaceId={workspace.id} additionalMenuItems={ourSettingsItems(workspace.id)}>
        <MembersSettings workspaceId={workspace.id} isOwner={workspace.role === "owner"} />
      </SettingsLayout>
    ) : (
      <UnknownWorkspaceScreen />
    );
  },
});

/**
 * The applications this **account** has connected, and disconnecting one.
 *
 * The address has no workspace in it, and that is the resource rather than an
 * oversight: an OAuth grant is held by a person, so there is one list per
 * account and disconnecting ends that application's access in every workspace
 * they are a member of. A workspace in the address would say the opposite of
 * what the button does — and this is the same argument `apiTokensRoute` makes
 * in the other direction, where a key really does belong to one workspace.
 *
 * The frame still needs *a* workspace, because the menu beside it links to that
 * workspace's API tokens. It is chosen the way `indexRoute` chooses where to
 * land — the stored one, otherwise the first — so the two screens agree about
 * which workspace the menu is pointing at. An account in no workspace still
 * gets the screen: grants are not a workspace's, and the frame is what it loses.
 */
const connectedApplicationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/settings/connected-applications",
  staticData: { title: "Connected Applications" },
  component: function ConnectedApplications() {
    const me = useMe();
    const [storedWorkspaceId] = useSelectedWorkspace();
    const workspaces = me.data?.workspaces ?? [];
    const frameWorkspace =
      workspaces.find((each) => each.id === storedWorkspaceId) ?? workspaces[0];

    return frameWorkspace ? (
      <SettingsLayout workspaceId={frameWorkspace.id} additionalMenuItems={ourSettingsItems(frameWorkspace.id)}>
        <ConnectedApplicationsSettings />
      </SettingsLayout>
    ) : (
      <div className="px-8 py-6">
        <ConnectedApplicationsSettings />
      </div>
    );
  },
});

/**
 * Where a person goes once they are signed in.
 *
 * Two callers arrive at `/login` needing to come back somewhere, and they say
 * so differently. The **consent screen** sends an explicit `?next=`. The
 * **authorization server** sends the whole authorization query instead, because
 * it knows nothing about this console's conventions — and that second case is
 * the one that was missing: without it a person who signed out mid-connection
 * finished the magic link and landed on the home page, with the client still
 * waiting.
 *
 * `next` wins when it is there. It is already narrowed to a same-origin path,
 * while the authorization return is built from a path this file owns, so
 * neither branch can send a browser off this origin.
 */
function landingAfterSignIn(next: string | undefined): string {
  return next ?? authorizationReturn(window.location.search) ?? "/";
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  // `?mode=register` is the same screen with different words, as before. An
  // unknown value is a sign-in, not an error page.
  //
  // `?next=` is where to land afterwards, and it exists for one caller: the
  // OAuth consent screen, which sends an unauthenticated visitor here and needs
  // them back at the authorization it interrupted. Only a **same-origin path**
  // is kept — a value carrying a scheme or a host is dropped rather than
  // corrected, because a login screen that forwards to wherever its query says
  // is the open redirect every phishing mail is looking for. The API applies
  // the same rule again on the link itself (`safeRedirectPath`); this is the
  // half that decides what the browser does before any mail is sent.
  //
  // `?error=` is how a Google round that failed reports back, since by then the
  // browser has been to two other sites and nothing of this app's state
  // survived. It is kept as it arrived and never shown: the screen maps the
  // codes it knows to sentences and falls back to a general one, so a value
  // from the query string cannot become words on the page.
  validateSearch: (
    search: Record<string, unknown>,
  ): { mode?: LoginMode; next?: string; error?: string } => ({
    mode: search.mode === "register" ? "register" : undefined,
    next: sameOriginPath(search.next),
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  beforeLoad: async ({ search }) => {
    if (await currentUser()) throw redirect({ href: landingAfterSignIn(search.next) });
  },
  component: function Login() {
    const { mode, next, error } = useSearch({ from: loginRoute.id });

    return (
      <Shell>
        <LoginScreen mode={mode ?? "login"} next={landingAfterSignIn(next)} failedProvider={error} />
      </Shell>
    );
  },
});

/**
 * `/auth/google`, which the server-rendered console answered with a page.
 *
 * It was a stub for as long as Google sign-in was one, and the stub is gone
 * with it (`sitebrew-api#43`) — but the path is not, because it is what the old
 * console's link pointed at and a person who kept it would otherwise get "that
 * page doesn't exist" for an address that used to work. Dropping it in the flip
 * to a single-page app was the one of the old Worker's six paths that changed
 * meaning, found in review of #8; this keeps that fix and updates where it
 * leads.
 *
 * A redirect and not a screen, because Google sign-in no longer *has* a page of
 * its own: it starts with a POST from the sign-in screen, which is where the
 * button lives. Landing there is the honest answer to this address.
 */
const googleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/google",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

/**
 * Exported so a test can mount the **real** tree against a memory history.
 *
 * The regression this is answering was not in a screen — every screen rendered
 * correctly — it was a path missing from the tree, which only a test that
 * navigates can see. Asserting the link's `href` and the screen's words
 * separately would have stayed green through it.
 */
/**
 * Where somebody decides whether to let an application act as them.
 *
 * Deliberately **not** under `authedRoute`: that layout draws the whole signed-in
 * frame — sidebar, workspace switcher, navigation — around its children, and a
 * consent question with the product's navigation around it invites the reader to
 * click away from a decision another program is waiting on. It is a card on its
 * own, like sign-in.
 *
 * It still needs a session, for a reason the layout could not have expressed
 * anyway: the answer is recorded against a person. An unauthenticated visitor is
 * sent to sign in **with this address as `next`**, so the authorization survives
 * the detour instead of being abandoned halfway.
 */
const consentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/consent",
  /**
   * The query is kept as a **string**, not parsed into fields.
   *
   * The authorization server put it there and identifies the pending request
   * from it; this page hands it back verbatim. Two values are read out for the
   * words on screen only — which client, which scopes — and reading is all they
   * are for. Rebuilding the query from parsed parts would make this screen a
   * second implementation of somebody else's state machine, and the first
   * parameter it did not know about would be dropped silently.
   */
  validateSearch: (search: Record<string, unknown>): { client_id?: string; scope?: string } => ({
    client_id: typeof search.client_id === "string" ? search.client_id : undefined,
    scope: typeof search.scope === "string" ? search.scope : undefined,
  }),
  beforeLoad: async () => {
    if (!(await currentUser())) {
      throw redirect({
        to: "/login",
        search: { next: `${window.location.pathname}${window.location.search}` },
      });
    }
  },
  component: function Consent() {
    const { client_id, scope } = useSearch({ from: consentRoute.id });

    return (
      <Shell>
        <ConsentScreen
          search={window.location.search.replace(/^\?/, "")}
          clientId={client_id}
          scopes={(scope ?? "").split(" ").filter(Boolean)}
        />
      </Shell>
    );
  },
});

/**
 * Where somebody picks which workspace an application acts in — reached from
 * `/oauth2/authorize` exactly the way `/consent` is, via `auth.ts`'s
 * `postLogin` hook, only when the account has more than one workspace and this
 * authorization has not already picked one.
 *
 * Not under `authedRoute`, and needs a session before it needs anything else,
 * for the same two reasons `consentRoute` gives: a decision another program is
 * waiting on should not sit inside the product's own navigation, and the
 * choice is recorded against a person, so an unauthenticated visitor is sent
 * to sign in first, with this address as `next`.
 */
const selectWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/select-workspace",
  // Same shape as `consentRoute`'s own `validateSearch`, and for the same
  // reason: `client_id` is read out of the carried-through authorize query
  // only to show which application is asking (`SelectWorkspaceScreen`'s own
  // client-name fetch), never rebuilt into anything sent back to the server.
  validateSearch: (search: Record<string, unknown>): { client_id?: string } => ({
    client_id: typeof search.client_id === "string" ? search.client_id : undefined,
  }),
  beforeLoad: async () => {
    if (!(await currentUser())) {
      throw redirect({
        to: "/login",
        search: { next: `${window.location.pathname}${window.location.search}` },
      });
    }
  },
  component: function SelectWorkspace() {
    const { client_id } = useSearch({ from: selectWorkspaceRoute.id });

    return (
      <Shell>
        <SelectWorkspaceScreen
          search={window.location.search.replace(/^\?/, "")}
          clientId={client_id}
        />
      </Shell>
    );
  },
});

/**
 * Where an interrupted authorization is picked back up after sign-in.
 *
 * It exists because the authorization query cannot travel as a `callbackURL`
 * unchanged — a percent-encoded colon gets it refused (`authorizationReturn`).
 * So the query arrives packed, this route unpacks it and hands the browser to
 * the authorization server.
 *
 * A full navigation, not a router one: `/v1/auth/*` is proxied to the API and
 * is not a route this app draws. And no session check — the person has just
 * come back from a magic link, and the authorization server is the thing
 * entitled to decide whether that session is good enough.
 */
/**
 * Where an invite e-mail's link lands (`sitebrew-api`'s `sendInviteMail`,
 * design note 0001 §4/§6, M3).
 *
 * Same shape as `consentRoute` and `selectWorkspaceRoute` just above: not
 * under `authedRoute` because the person clicking this link may not have a
 * session yet, but it still needs one before the screen can call
 * `/invites/accept` (that endpoint identifies the invite by the caller's own
 * verified e-mail, not by anything in the URL) — so an unauthenticated
 * visitor is sent to sign in first, with this address as `next`, the same
 * way the other two send theirs.
 */
const inviteAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/accept",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  beforeLoad: async () => {
    if (!(await currentUser())) {
      throw redirect({
        to: "/login",
        search: { next: `${window.location.pathname}${window.location.search}` },
      });
    }
  },
  component: function InviteAccept() {
    const { token } = useSearch({ from: inviteAcceptRoute.id });
    return (
      <Shell>
        <InviteAcceptScreen token={token} />
      </Shell>
    );
  },
});

const resumeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/continue",
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search.q === "string" ? search.q : undefined,
  }),
  component: function Resume() {
    const { q } = useSearch({ from: resumeRoute.id });

    useEffect(() => {
      // An unreadable blob is not an error page: it is somebody whose link went
      // stale, and the console's own front door is the honest place for them.
      window.location.replace(resumedAuthorization(q) ?? "/");
    }, [q]);

    return null;
  },
});

export const routeTree = rootRoute.addChildren([
  authedRoute.addChildren([
    indexRoute,
    sitesRoute,
    newSiteRoute,
    siteRoute,
    apiTokensRoute,
    membersRoute,
    connectedApplicationsRoute,
  ]),
  loginRoute,
  consentRoute,
  selectWorkspaceRoute,
  inviteAcceptRoute,
  resumeRoute,
  googleRoute,
]);

const router = createRouter({
  routeTree,
  defaultPreload: false,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
